/*
 * Runtime branding. The panel starts as Hosterr; /api/brand says whose
 * brand to show instead (the host's for the sign-in pages, the current org's
 * agency inside the app), and this applies it without a rebuild: the accent
 * becomes the brand-* color tokens, plus the logo, favicon and name.
 */
import { useSyncExternalStore } from "react";
import { brandRamp, parseHex, SHADES, solidBrand } from "./color";

export type Brand = {
  /** The org this brand belongs to: the agency, or the platform org. Null before /api/brand answers. */
  orgId: string | null;
  /** "Hosterr", or the agency's display name. */
  name: string;
  logoLight: string | null;
  logoDark: string | null;
  favicon: string | null;
  /** A hex color ("#d54628", Hosterr's orange, by default); null means the same. */
  accent: string | null;
  supportEmail: string | null;
  supportUrl: string | null;
  emailFromName?: string;
  /** Hosterr's own brand (no agency). */
  platform: boolean;
};

export const HOSTERR_BRAND: Brand = {
  orgId: null,
  name: "Hosterr",
  logoLight: null,
  logoDark: null,
  favicon: null,
  accent: null,
  supportEmail: null,
  supportUrl: null,
  platform: true,
};

const CACHE_KEY = "hx-brand";
let current: Brand = readCache() ?? HOSTERR_BRAND;
const listeners = new Set<() => void>();

function readCache(): Brand | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const b = raw ? (JSON.parse(raw) as Brand) : null;
    return b && typeof b.name === "string" ? b : null;
  } catch {
    return null;
  }
}

function writeCache(b: Brand) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(b));
  } catch {
    /* the brand still applies for this visit */
  }
}

const STOCK_FAVICONS = new Map<HTMLLinkElement, string>();

function applyTokens(b: Brand) {
  const style = document.documentElement.style;
  // Hosterr's orange keeps its hand-tuned ramp from styles.css, whoever's brand it is.
  const check = b.accent && b.accent.toLowerCase() !== "#d54628" ? brandRamp(b.accent) : null;
  if (!check) {
    // Hosterr's own hand-tuned ramp from styles.css.
    for (const s of SHADES) style.removeProperty(`--color-brand-${s}`);
    style.removeProperty("--hx-brand-rgb");
    style.removeProperty("--hx-brand-solid");
    style.removeProperty("--hx-on-brand");
    return;
  }
  for (const s of SHADES) style.setProperty(`--color-brand-${s}`, check.ramp[s]);
  const rgb = parseHex(check.ramp[500]);
  if (rgb) style.setProperty("--hx-brand-rgb", rgb.join(" "));
  // "Where you are" as a solid fill: the agency's color itself, with text that reads on it.
  const solid = solidBrand(b.accent!, check.ramp);
  style.setProperty("--hx-brand-solid", solid.fill);
  style.setProperty("--hx-on-brand", solid.text);
}

function applyFavicon(b: Brand) {
  const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];
  for (const l of links) {
    if (!STOCK_FAVICONS.has(l)) STOCK_FAVICONS.set(l, l.href);
    l.href = b.favicon && !b.platform ? b.favicon : STOCK_FAVICONS.get(l)!;
  }
}

/** Show a brand now. The same brand again is a no-op, so calling it on every fetch is cheap. */
export function applyBrand(b: Brand) {
  const same = JSON.stringify(b) === JSON.stringify(current);
  current = b;
  applyTokens(b);
  applyFavicon(b);
  if (!same) {
    writeCache(b);
    listeners.forEach((l) => l());
  }
}

/** Apply the last brand this browser saw, before the first paint, so an agency's clients don't see orange flash first. */
export function applyCachedBrand() {
  applyTokens(current);
  applyFavicon(current);
}

export function currentBrand(): Brand {
  return current;
}

/**
 * Who to ask for help: the agency, with its own support email or page, or Hosterr. An agency without either has
 * none to show (its clients never see Hosterr's).
 */
export function supportContact(b: Brand): { name: string; email: string | null; url: string | null } {
  // Hosterr's own address until /api/brand carries the platform's support email.
  return { name: b.platform ? HOSTERR_BRAND.name : b.name, email: b.supportEmail ?? (b.platform ? "hello@hosterr.com" : null), url: b.supportUrl };
}

/**
 * The product name to show: "Hosterr" or the agency's, for any "Hosterr" a person reads (an agency's clients only
 * ever see the agency). `brandName()` outside React (api/client.ts); `useBrandName()` in components, which re-render
 * when the brand arrives. Hosterr's own staff pages may say Hosterr outright.
 */
export const brandName = () => current.name || HOSTERR_BRAND.name;

export function useBrand(): Brand {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
  );
}

/** The product name to show, in a component: "Hosterr" or the agency's (see `brandName`). */
export const useBrandName = (): string => useBrand().name || HOSTERR_BRAND.name;

/**
 * Who to ask for what an account can't do itself ("Ask Hosterr for one"): an agency's own people ask Hosterr,
 * whatever brand their panel shows; everyone else asks the brand they see (an agency's clients ask the agency).
 */
export const useAskName = (orgKind: string | null | undefined): string => {
  const name = useBrandName();
  return orgKind === "agency" ? HOSTERR_BRAND.name : name;
};
